import { MutableState } from 'src/replicant/State';
import {
  match2BundleSaleGetCurrentEventItems,
  match2BundleSaleGetCurrentSchedule,
  match2BundleSaleGetPackRewardSet,
  match2BundleSaleIsFreeProduct,
} from './match2-bundlesale.getters';
import {
  BundleSaleProductId,
  bundleSaleProducts,
} from './match2-bundlesale.ruleset';
import * as Match2 from '..';
import { ProductId } from 'src/replicant/iap';
import {
  CurrencyGrantOpts,
  CurrencyReplicantAPI,
} from 'src/replicant/inventory/inventory.utils';

/**
 * Reset bundle sale state
 */
export function match2BundleSaleReset(state: MutableState): void {
  state.match2.bundleSale = {
    lastPurchasedAt: 0,
    purchased: [],
  };
}

/**
 * Purchase a bundle sale product
 */
export function match2BundleSalePurchase(
  state: MutableState,
  productId: BundleSaleProductId,
  api: CurrencyReplicantAPI,
): boolean {
  const now = api.date.now();
  const schedule = match2BundleSaleGetCurrentSchedule(now);

  // Skip if no event is active
  if (!schedule) return false;

  const items = match2BundleSaleGetCurrentEventItems(state, now);

  // Skip product is not in event items list
  if (!items.includes(productId)) return false;

  // Reset bundle sale if event changed
  if (
    !state.match2.bundleSale ||
    state.match2.bundleSale.lastPurchasedAt < schedule?.startTime
  ) {
    match2BundleSaleReset(state);
  }

  //   Check if bundle sale has been created
  if (!state.match2.bundleSale) {
    throw new Error('Failed to reset bundle sale');
  }

  // Skip if product is already purchased
  if (state.match2.bundleSale.purchased.includes(productId)) {
    return false;
  }

  // Add purchased product to the list
  state.match2.bundleSale.purchased.push(productId);

  // Set the last purchased timestamp
  state.match2.bundleSale.lastPurchasedAt = now;

  // Claim product rewards straight away if it's a free product
  if (match2BundleSaleIsFreeProduct(productId)) {
    match2BundleSaleClaimProductRewards(state, productId, api);
  }

  return true;
}

/**
 * Claim bundle sale rewards, only for bundle sale packs
 * This is not including free and paid gems, as they are given as regular gems rewards,
 * so only extra rewards (darts, bombs, unlimited energy, etc) are handled here.
 */
export function match2BundleSaleClaimProductRewards(
  state: MutableState,
  productId: ProductId | BundleSaleProductId,
  api: CurrencyReplicantAPI,
  opts?: CurrencyGrantOpts,
): void {
  // Skip if product id is not a bundle sale pack
  if (!bundleSaleProducts.includes(productId as BundleSaleProductId)) return;

  // Process endless sale.
  match2BundleSalePurchase(state, productId as BundleSaleProductId, api);

  const now = api.date.now();
  const rewards = match2BundleSaleGetPackRewardSet(
    productId as BundleSaleProductId,
    now,
  );
  if (rewards.length === 0) return;

  Match2.modifiers.grantMatch2Rewards(
    state,
    rewards,
    {
      feature: 'purchase',
      $subFeature: `${productId}`,
    },
    api,
    opts,
  );
}
